import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apagar, editarJson, enviarJson, enviarSemConteudo, pedirJson } from "./api";
import { ROTULO_DA_URGENCIA, ROTULO_DO_ESTADO } from "./tickets";

/* Automação e integração: macros, gatilhos, webhooks de saída e tokens de API.
   O contrato é o de `api/app/dominios/automacao/`. Segredo de webhook e token de API só existem
   na resposta da criação (e da rotação do segredo): esta camada os devolve à tela uma vez e nunca
   os guarda no cache de listagens. */

const uuid = z.string().uuid();

const esquemaMudancasDaMacro = z.object({
  estado: z.string().optional(),
  urgencia: z.string().optional(),
  equipe_id: uuid.optional(),
  responsavel_id: uuid.optional(),
  categoria: z.string().optional(),
  servico: z.string().optional(),
  adicionar_tags: z.array(z.string()).optional(),
  remover_tags: z.array(z.string()).optional(),
});

const esquemaMacro = z.object({
  id: uuid,
  nome: z.string(),
  texto: z.string().nullable(),
  tipo_de_resposta: z.enum(["publica", "interna"]),
  mudancas: esquemaMudancasDaMacro,
  compartilhada: z.boolean(),
  criado_por: uuid.nullable(),
  minha: z.boolean(),
  atualizada_em: z.string(),
});

const esquemaMudancaPrevista = z.object({
  campo: z.string(),
  de: z.string().nullable(),
  para: z.string().nullable(),
});
const esquemaPreviaDaMacro = z.object({
  texto: z.string().nullable(),
  tipo_de_resposta: z.enum(["publica", "interna"]),
  mudancas: z.array(esquemaMudancaPrevista),
});
const esquemaResultadoDaMacro = z.object({
  acao_id: uuid.nullable(),
  mudancas: z.array(esquemaMudancaPrevista),
});
const esquemaPlaceholder = z.object({ chave: z.string(), descricao: z.string() });

const esquemaGatilho = z.object({
  id: uuid,
  nome: z.string(),
  evento: z.enum(["ticket_aberto", "acao_publica", "estado_alterado", "sla_alerta"]),
  modo: z.enum(["todas", "qualquer"]),
  condicoes: z.object({
    organizacao_id: uuid.optional(),
    urgencia: z.string().optional(),
    categoria: z.string().optional(),
    servico: z.string().optional(),
    estado: z.string().optional(),
    texto_contem: z.string().optional(),
    tag: z.string().optional(),
  }),
  acoes: z.array(z.object({ tipo: z.string() }).passthrough()),
  ordem: z.number().int(),
  ativo: z.boolean(),
  atualizado_em: z.string(),
});

const esquemaExecucao = z.object({
  ticket_id: uuid,
  numero_do_ticket: z.number().int().nullable(),
  tipo: z.string(),
  detalhe: z.record(z.string(), z.unknown()),
  ocorrido_em: z.string(),
});

const esquemaWebhook = z.object({
  id: uuid,
  nome: z.string(),
  url: z.string(),
  eventos: z.array(z.string()),
  ativo: z.boolean(),
  escopo: z.enum(["todas", "organizacao"]),
  organizacao_id: uuid.nullable(),
  criado_em: z.string(),
  segredo_rotacionado_em: z.string().nullable(),
});
const esquemaWebhookCriado = esquemaWebhook.extend({ segredo: z.string() });
const esquemaSegredo = z.object({ segredo: z.string() });

const esquemaTentativa = z.object({
  numero: z.number().int(),
  status_http: z.number().int().nullable(),
  erro: z.string().nullable(),
  duracao_ms: z.number().int(),
  tentada_em: z.string(),
});
const esquemaEntrega = z.object({
  id: uuid,
  webhook_id: uuid,
  evento: z.string(),
  estado: z.enum(["pendente", "entregue", "falhou"]),
  tentativas: z.number().int(),
  proxima_tentativa_em: z.string(),
  ultimo_status: z.number().int().nullable(),
  ultimo_erro: z.string().nullable(),
  entregue_em: z.string().nullable(),
  criada_em: z.string(),
  tentativas_feitas: z.array(esquemaTentativa),
});

const esquemaToken = z.object({
  id: uuid,
  organizacao_id: uuid.nullable(),
  nome: z.string(),
  prefixo: z.string(),
  escopos: z.array(z.string()),
  criado_em: z.string(),
  ultimo_uso_em: z.string().nullable(),
  revogado_em: z.string().nullable(),
});
const esquemaTokenCriado = esquemaToken.extend({ token: z.string() });

const esquemaEventos = z.object({
  gatilhos: z.array(z.string()),
  webhooks: z.array(z.string()),
});

const esquemaOrganizacao = z.object({
  id: uuid,
  razao_social: z.string(),
  nome_fantasia: z.string().nullable(),
});

export type Macro = z.infer<typeof esquemaMacro>;
export type MudancasDaMacro = z.infer<typeof esquemaMudancasDaMacro>;
export type MudancaPrevista = z.infer<typeof esquemaMudancaPrevista>;
export type PreviaDaMacro = z.infer<typeof esquemaPreviaDaMacro>;
export type Placeholder = z.infer<typeof esquemaPlaceholder>;
export type Gatilho = z.infer<typeof esquemaGatilho>;
export type ExecucaoDeGatilho = z.infer<typeof esquemaExecucao>;
export type Webhook = z.infer<typeof esquemaWebhook>;
export type WebhookCriado = z.infer<typeof esquemaWebhookCriado>;
export type EntregaDeWebhook = z.infer<typeof esquemaEntrega>;
export type TokenDeApi = z.infer<typeof esquemaToken>;
export type TokenCriado = z.infer<typeof esquemaTokenCriado>;
export type OrganizacaoDaLista = z.infer<typeof esquemaOrganizacao>;
export type EventoDeGatilho = Gatilho["evento"];
export type ModoDoGatilho = Gatilho["modo"];
export type AcaoDoGatilho = Gatilho["acoes"][number];
export type CondicoesDoGatilho = Gatilho["condicoes"];

const CHAVE = ["automacao"] as const;

/* ------------------------------------------------------------------ rótulos */

export const ROTULO_DO_EVENTO_DE_GATILHO: Record<EventoDeGatilho, string> = {
  ticket_aberto: "Chamado aberto",
  acao_publica: "Resposta pública",
  estado_alterado: "Mudança de estado",
  sla_alerta: "Alerta de SLA",
};

export const ROTULO_DO_EVENTO_DE_WEBHOOK: Record<string, string> = {
  "ticket.aberto": "Chamado aberto",
  "ticket.acao_publica": "Resposta pública",
  "ticket.estado_alterado": "Mudança de estado",
  "ticket.sla_alerta": "Alerta de SLA",
};

export const ROTULO_DO_TIPO_DE_ACAO: Record<string, string> = {
  atribuir_equipe: "Atribuir equipe",
  atribuir_responsavel: "Atribuir responsável",
  mudar_urgencia: "Mudar urgência",
  mudar_estado: "Mudar estado",
  nota_interna: "Adicionar nota interna",
  enviar_email: "Enviar e-mail",
};

export const ROTULO_DO_DESTINO_DE_EMAIL: Record<string, string> = {
  solicitante: "quem abriu o chamado",
  responsavel: "o responsável",
  endereco: "um endereço fixo",
};

export const ROTULO_DO_ESCOPO_DE_TOKEN: Record<string, string> = {
  "chamados:ler": "Ler chamados",
  "chamados:abrir": "Abrir chamados",
};

export const ROTULO_DO_CAMPO_DE_MACRO: Record<string, string> = {
  estado: "Estado",
  urgencia: "Urgência",
  equipe_id: "Equipe",
  responsavel_id: "Responsável",
  categoria: "Categoria",
  servico: "Serviço",
  tags: "Tags",
};

export const ROTULO_DA_EXECUCAO: Record<string, string> = {
  gatilho_executado: "Executado",
  gatilho_falhou: "Falhou",
  gatilho_bloqueado: "Bloqueado",
};

export const ROTULO_DO_MOTIVO_DE_BLOQUEIO: Record<string, string> = {
  laco: "o gatilho já estava na cadeia (laço)",
  profundidade: "a cadeia passou do limite de níveis",
  limite: "limite de gatilhos por ato",
};

export const ROTULO_DA_ENTREGA: Record<EntregaDeWebhook["estado"], string> = {
  pendente: "Na fila",
  entregue: "Entregue",
  falhou: "Falhou",
};

/* ------------------------------------------------------------------ helpers puros */

/** Valor legível de uma mudança de campo da macro (estado e urgência têm rótulo próprio). */
export function valorLegivel(
  campo: string,
  valor: string | null,
  nomes: Record<string, string> = {},
): string {
  if (valor === null || valor === "") return "vazio";
  if (campo === "estado") return (ROTULO_DO_ESTADO as Record<string, string>)[valor] ?? valor;
  if (campo === "urgencia") return (ROTULO_DA_URGENCIA as Record<string, string>)[valor] ?? valor;
  return nomes[valor] ?? valor;
}

/** "Estado: Novo → Resolvido" */
export function descreverMudanca(
  mudanca: MudancaPrevista,
  nomes: Record<string, string> = {},
): string {
  const rotulo = ROTULO_DO_CAMPO_DE_MACRO[mudanca.campo] ?? mudanca.campo;
  return `${rotulo}: ${valorLegivel(mudanca.campo, mudanca.de, nomes)} → ${valorLegivel(mudanca.campo, mudanca.para, nomes)}`;
}

export function resumirCondicoes(
  condicoes: CondicoesDoGatilho,
  modo: ModoDoGatilho,
  nomes: Record<string, string> = {},
): string {
  const partes: string[] = [];
  if (condicoes.organizacao_id) {
    partes.push(`organização é ${nomes[condicoes.organizacao_id] ?? condicoes.organizacao_id}`);
  }
  if (condicoes.urgencia) partes.push(`urgência é ${valorLegivel("urgencia", condicoes.urgencia).toLocaleLowerCase("pt-BR")}`);
  if (condicoes.categoria) partes.push(`categoria é ${condicoes.categoria}`);
  if (condicoes.servico) partes.push(`serviço é ${condicoes.servico}`);
  if (condicoes.estado) partes.push(`estado é ${valorLegivel("estado", condicoes.estado).toLocaleLowerCase("pt-BR")}`);
  if (condicoes.texto_contem) partes.push(`o texto contém “${condicoes.texto_contem}”`);
  if (condicoes.tag) partes.push(`o chamado tem a tag “${condicoes.tag}”`);
  if (partes.length === 0) return "sempre";
  return partes.join(modo === "todas" ? " e " : " ou ");
}

export function resumirAcao(acao: AcaoDoGatilho, nomes: Record<string, string> = {}): string {
  const dado = acao as Record<string, unknown>;
  switch (acao.tipo) {
    case "atribuir_equipe":
      return `atribuir à equipe ${nomes[String(dado.equipe_id)] ?? "escolhida"}`;
    case "atribuir_responsavel":
      return `atribuir a ${nomes[String(dado.responsavel_id)] ?? "o responsável escolhido"}`;
    case "mudar_urgencia":
      return `mudar a urgência para ${valorLegivel("urgencia", String(dado.urgencia)).toLocaleLowerCase("pt-BR")}`;
    case "mudar_estado":
      return `mudar o estado para ${valorLegivel("estado", String(dado.estado)).toLocaleLowerCase("pt-BR")}`;
    case "nota_interna":
      return "adicionar uma nota interna";
    case "enviar_email":
      return `enviar e-mail para ${ROTULO_DO_DESTINO_DE_EMAIL[String(dado.destino)] ?? "o destinatário"}`;
    default:
      return acao.tipo;
  }
}

/** "Quando Chamado aberto e urgência é alta, então atribuir à equipe X e adicionar uma nota interna." */
export function resumirGatilho(gatilho: Gatilho, nomes: Record<string, string> = {}): string {
  const quando = ROTULO_DO_EVENTO_DE_GATILHO[gatilho.evento];
  const condicoes = resumirCondicoes(gatilho.condicoes, gatilho.modo, nomes);
  const acoes = gatilho.acoes.map((acao) => resumirAcao(acao, nomes)).join(" e ");
  return `Quando ${quando.toLocaleLowerCase("pt-BR")} (${condicoes}), então ${acoes}.`;
}

export interface FormularioDeAcao {
  tipo: string;
  equipe_id: string;
  responsavel_id: string;
  urgencia: string;
  estado: string;
  destino: string;
  endereco: string;
  assunto: string;
  texto: string;
}

export const ACAO_EM_BRANCO: FormularioDeAcao = {
  tipo: "nota_interna",
  equipe_id: "",
  responsavel_id: "",
  urgencia: "media",
  estado: "em_atendimento",
  destino: "solicitante",
  endereco: "",
  assunto: "",
  texto: "",
};

export interface FormularioDeGatilho {
  nome: string;
  evento: EventoDeGatilho;
  modo: ModoDoGatilho;
  organizacao_id: string;
  urgencia: string;
  categoria: string;
  servico: string;
  estado: string;
  texto_contem: string;
  tag: string;
  acoes: FormularioDeAcao[];
  ordem: string;
  ativo: boolean;
}

export const GATILHO_EM_BRANCO: FormularioDeGatilho = {
  nome: "",
  evento: "ticket_aberto",
  modo: "todas",
  organizacao_id: "",
  urgencia: "",
  categoria: "",
  servico: "",
  estado: "",
  texto_contem: "",
  tag: "",
  acoes: [{ ...ACAO_EM_BRANCO }],
  ordem: "100",
  ativo: true,
};

export function acaoParaCorpo(acao: FormularioDeAcao): Record<string, unknown> {
  switch (acao.tipo) {
    case "atribuir_equipe":
      return { tipo: acao.tipo, equipe_id: acao.equipe_id };
    case "atribuir_responsavel":
      return { tipo: acao.tipo, responsavel_id: acao.responsavel_id };
    case "mudar_urgencia":
      return { tipo: acao.tipo, urgencia: acao.urgencia };
    case "mudar_estado":
      return { tipo: acao.tipo, estado: acao.estado };
    case "enviar_email":
      return {
        tipo: acao.tipo,
        destino: acao.destino,
        endereco: acao.destino === "endereco" ? acao.endereco.trim() : null,
        assunto: acao.assunto.trim(),
        texto: acao.texto.trim(),
      };
    default:
      return { tipo: "nota_interna", texto: acao.texto.trim() };
  }
}

export function corpoDoGatilho(form: FormularioDeGatilho): Record<string, unknown> {
  const condicoes: Record<string, string> = {};
  for (const campo of ["organizacao_id", "urgencia", "categoria", "servico", "estado", "texto_contem", "tag"] as const) {
    const valor = form[campo].trim();
    if (valor) condicoes[campo] = valor;
  }
  const ordem = Number.parseInt(form.ordem, 10);
  return {
    nome: form.nome.trim(),
    evento: form.evento,
    modo: form.modo,
    condicoes,
    acoes: form.acoes.map(acaoParaCorpo),
    ordem: Number.isFinite(ordem) ? ordem : 100,
    ativo: form.ativo,
  };
}

export function formularioDoGatilho(gatilho: Gatilho): FormularioDeGatilho {
  return {
    nome: gatilho.nome,
    evento: gatilho.evento,
    modo: gatilho.modo,
    organizacao_id: gatilho.condicoes.organizacao_id ?? "",
    urgencia: gatilho.condicoes.urgencia ?? "",
    categoria: gatilho.condicoes.categoria ?? "",
    servico: gatilho.condicoes.servico ?? "",
    estado: gatilho.condicoes.estado ?? "",
    texto_contem: gatilho.condicoes.texto_contem ?? "",
    tag: gatilho.condicoes.tag ?? "",
    acoes: gatilho.acoes.map((acao) => {
      const dado = acao as Record<string, unknown>;
      const texto = (chave: string) => (typeof dado[chave] === "string" ? (dado[chave] as string) : "");
      return {
        ...ACAO_EM_BRANCO,
        tipo: acao.tipo,
        equipe_id: texto("equipe_id"),
        responsavel_id: texto("responsavel_id"),
        urgencia: texto("urgencia") || ACAO_EM_BRANCO.urgencia,
        estado: texto("estado") || ACAO_EM_BRANCO.estado,
        destino: texto("destino") || ACAO_EM_BRANCO.destino,
        endereco: texto("endereco"),
        assunto: texto("assunto"),
        texto: texto("texto"),
      };
    }),
    ordem: String(gatilho.ordem),
    ativo: gatilho.ativo,
  };
}

/** Primeiro problema do formulário do gatilho, ou `null`. */
export function validarGatilho(form: FormularioDeGatilho): string | null {
  if (form.nome.trim().length < 2) return "Dê um nome ao gatilho.";
  if (form.acoes.length === 0) return "Inclua ao menos uma ação.";
  for (const acao of form.acoes) {
    if (acao.tipo === "atribuir_equipe" && !acao.equipe_id) return "Escolha a equipe da ação.";
    if (acao.tipo === "atribuir_responsavel" && !acao.responsavel_id) return "Escolha o responsável da ação.";
    if (acao.tipo === "nota_interna" && !acao.texto.trim()) return "Escreva o texto da nota interna.";
    if (acao.tipo === "enviar_email") {
      if (!acao.assunto.trim() || !acao.texto.trim()) return "O e-mail precisa de assunto e texto.";
      if (acao.destino === "endereco" && !/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(acao.endereco.trim())) {
        return "Informe um e-mail válido para o destino.";
      }
    }
  }
  return null;
}

export interface FormularioDeMacro {
  nome: string;
  texto: string;
  tipo_de_resposta: "publica" | "interna";
  compartilhada: boolean;
  estado: string;
  urgencia: string;
  equipe_id: string;
  responsavel_id: string;
  categoria: string;
  servico: string;
  /** Tags separadas por vírgula. */
  adicionar_tags: string;
  remover_tags: string;
}

/** "bug erp, vip ,," → ["bug erp", "vip"]: a API normaliza de novo; aqui só se separa e limpa. */
export function separarTags(texto: string): string[] {
  return texto
    .split(",")
    .map((tag) => tag.trim())
    .filter((tag) => tag !== "");
}

export const MACRO_EM_BRANCO: FormularioDeMacro = {
  nome: "",
  texto: "",
  tipo_de_resposta: "publica",
  compartilhada: false,
  estado: "",
  urgencia: "",
  equipe_id: "",
  responsavel_id: "",
  categoria: "",
  servico: "",
  adicionar_tags: "",
  remover_tags: "",
};

export function corpoDaMacro(form: FormularioDeMacro): Record<string, unknown> {
  const mudancas: Record<string, string | string[]> = {};
  for (const campo of ["estado", "urgencia", "equipe_id", "responsavel_id", "categoria", "servico"] as const) {
    if (form[campo].trim()) mudancas[campo] = form[campo].trim();
  }
  for (const campo of ["adicionar_tags", "remover_tags"] as const) {
    const tags = separarTags(form[campo]);
    if (tags.length > 0) mudancas[campo] = tags;
  }
  return {
    nome: form.nome.trim(),
    texto: form.texto.trim() ? form.texto.trim() : null,
    tipo_de_resposta: form.tipo_de_resposta,
    compartilhada: form.compartilhada,
    mudancas,
  };
}

export function formularioDaMacro(macro: Macro): FormularioDeMacro {
  return {
    nome: macro.nome,
    texto: macro.texto ?? "",
    tipo_de_resposta: macro.tipo_de_resposta,
    compartilhada: macro.compartilhada,
    estado: macro.mudancas.estado ?? "",
    urgencia: macro.mudancas.urgencia ?? "",
    equipe_id: macro.mudancas.equipe_id ?? "",
    responsavel_id: macro.mudancas.responsavel_id ?? "",
    categoria: macro.mudancas.categoria ?? "",
    servico: macro.mudancas.servico ?? "",
    adicionar_tags: (macro.mudancas.adicionar_tags ?? []).join(", "),
    remover_tags: (macro.mudancas.remover_tags ?? []).join(", "),
  };
}

export function validarMacro(form: FormularioDeMacro): string | null {
  if (form.nome.trim().length < 2) return "Dê um nome à macro.";
  const temMudanca = [
    form.estado,
    form.urgencia,
    form.equipe_id,
    form.responsavel_id,
    form.categoria,
    form.servico,
    form.adicionar_tags,
    form.remover_tags,
  ].some((valor) => valor.trim() !== "");
  const repetidas = separarTags(form.adicionar_tags).filter((tag) =>
    separarTags(form.remover_tags).some((outra) => outra.toLocaleLowerCase("pt-BR") === tag.toLocaleLowerCase("pt-BR")),
  );
  if (repetidas.length > 0) return `A tag “${repetidas[0]}” não pode ser adicionada e retirada pela mesma macro.`;
  if (!form.texto.trim() && !temMudanca) {
    return "Uma macro precisa de um texto de resposta ou de uma mudança de campo.";
  }
  return null;
}

/** Validação do endereço do webhook antes de enviar; a API repete a conferência (e é mais dura em produção). */
export function validarUrlDoWebhook(url: string): string | null {
  const texto = url.trim();
  let analisada: URL;
  try {
    analisada = new URL(texto);
  } catch {
    return "Informe o endereço completo do destino, começando com https://.";
  }
  if (analisada.protocol !== "https:" && analisada.protocol !== "http:") {
    return "Informe o endereço completo do destino, começando com https://.";
  }
  if (analisada.username || analisada.password) return "O endereço não pode carregar usuário e senha.";
  return null;
}

export const ESCOPOS_DE_TOKEN = ["chamados:ler", "chamados:abrir"] as const;

/** Frase curta do estado de uma entrega, para a linha do histórico. */
export function resumirEntrega(entrega: EntregaDeWebhook): string {
  if (entrega.estado === "entregue") return `Entregue com ${entrega.ultimo_status ?? "sucesso"} após ${entrega.tentativas} tentativa(s).`;
  const motivo = entrega.ultimo_erro ?? (entrega.ultimo_status ? `O destino respondeu ${entrega.ultimo_status}.` : "Ainda não tentada.");
  return entrega.estado === "falhou"
    ? `Esgotou as tentativas (${entrega.tentativas}). ${motivo}`
    : `${entrega.tentativas} tentativa(s). ${motivo}`;
}

/** Copia para a área de transferência; devolve se conseguiu (navegador sem permissão devolve `false`). */
export async function copiarTexto(texto: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    return false;
  }
}

export function quandoLegivel(valor: string | null): string {
  if (!valor) return "—";
  const data = new Date(valor);
  return Number.isNaN(data.getTime())
    ? valor
    : data.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

/* ------------------------------------------------------------------ hooks */

export function useEventosDeAutomacao(enabled = true) {
  return useQuery({
    queryKey: [...CHAVE, "eventos"],
    queryFn: ({ signal }) => pedirJson("/automacao/eventos", esquemaEventos, { signal }),
    enabled,
  });
}

export function usePlaceholders(enabled = true) {
  return useQuery({
    queryKey: [...CHAVE, "placeholders"],
    queryFn: ({ signal }) => pedirJson("/automacao/placeholders", z.array(esquemaPlaceholder), { signal }),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useOrganizacoesParaAutomacao(enabled = true) {
  return useQuery({
    queryKey: [...CHAVE, "organizacoes"],
    queryFn: ({ signal }) => pedirJson("/organizacoes?limite=200", z.array(esquemaOrganizacao), { signal }),
    enabled,
  });
}

export function useMacros(enabled = true) {
  return useQuery({
    queryKey: [...CHAVE, "macros"],
    queryFn: ({ signal }) => pedirJson("/automacao/macros", z.array(esquemaMacro), { signal }),
    enabled,
  });
}

export function useCriarMacro() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: Record<string, unknown>) => enviarJson("/automacao/macros", esquemaMacro, corpo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "macros"] }),
  });
}

export function useAlterarMacro() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ id, corpo }: { id: string; corpo: Record<string, unknown> }) =>
      editarJson(`/automacao/macros/${encodeURIComponent(id)}`, esquemaMacro, corpo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "macros"] }),
  });
}

export function useArquivarMacro() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apagar(`/automacao/macros/${encodeURIComponent(id)}`),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "macros"] }),
  });
}

export function usePreviaDaMacro() {
  return useMutation({
    mutationFn: ({ macroId, ticketId }: { macroId: string; ticketId: string }) =>
      enviarJson(`/automacao/macros/${encodeURIComponent(macroId)}/previa`, esquemaPreviaDaMacro, {
        ticket_id: ticketId,
      }),
  });
}

export function useAplicarMacro() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ macroId, ticketId, texto }: { macroId: string; ticketId: string; texto?: string | null }) =>
      enviarJson(`/automacao/macros/${encodeURIComponent(macroId)}/aplicar`, esquemaResultadoDaMacro, {
        ticket_id: ticketId,
        ...(texto ? { texto } : {}),
      }),
    onSuccess: (_resultado, { ticketId }) => {
      void cliente.invalidateQueries({ queryKey: ["tickets"] });
      void cliente.invalidateQueries({ queryKey: ["tickets", ticketId] });
    },
  });
}

export function useGatilhos(enabled = true) {
  return useQuery({
    queryKey: [...CHAVE, "gatilhos"],
    queryFn: ({ signal }) => pedirJson("/automacao/gatilhos", z.array(esquemaGatilho), { signal }),
    enabled,
  });
}

export function useExecucoesDeGatilhos(enabled = true) {
  return useQuery({
    queryKey: [...CHAVE, "gatilhos", "execucoes"],
    queryFn: ({ signal }) =>
      pedirJson("/automacao/gatilhos/execucoes", z.array(esquemaExecucao), { signal }),
    enabled,
  });
}

export function useCriarGatilho() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: Record<string, unknown>) => enviarJson("/automacao/gatilhos", esquemaGatilho, corpo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "gatilhos"] }),
  });
}

export function useAlterarGatilho() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ id, corpo }: { id: string; corpo: Record<string, unknown> }) =>
      editarJson(`/automacao/gatilhos/${encodeURIComponent(id)}`, esquemaGatilho, corpo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "gatilhos"] }),
  });
}

export function useExcluirGatilho() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apagar(`/automacao/gatilhos/${encodeURIComponent(id)}`),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "gatilhos"] }),
  });
}

export function useWebhooks(enabled = true) {
  return useQuery({
    queryKey: [...CHAVE, "webhooks"],
    queryFn: ({ signal }) => pedirJson("/automacao/webhooks", z.array(esquemaWebhook), { signal }),
    enabled,
  });
}

export function useCriarWebhook() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: {
      nome: string;
      url: string;
      eventos: string[];
      escopo: "todas" | "organizacao";
      organizacao_id?: string;
    }) =>
      enviarJson("/automacao/webhooks", esquemaWebhookCriado, corpo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "webhooks"] }),
  });
}

export function useAlterarWebhook() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ id, corpo }: { id: string; corpo: { nome?: string; url?: string; eventos?: string[]; ativo?: boolean } }) =>
      editarJson(`/automacao/webhooks/${encodeURIComponent(id)}`, esquemaWebhook, corpo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "webhooks"] }),
  });
}

export function useGirarSegredoDoWebhook() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      enviarJson(`/automacao/webhooks/${encodeURIComponent(id)}/segredo`, esquemaSegredo, {}),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "webhooks"] }),
  });
}

export function useExcluirWebhook() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apagar(`/automacao/webhooks/${encodeURIComponent(id)}`),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "webhooks"] }),
  });
}

export function useEntregasDoWebhook(id: string, enabled = true) {
  return useQuery({
    queryKey: [...CHAVE, "webhooks", id, "entregas"],
    queryFn: ({ signal }) =>
      pedirJson(`/automacao/webhooks/${encodeURIComponent(id)}/entregas`, z.array(esquemaEntrega), { signal }),
    enabled: enabled && Boolean(id),
  });
}

export function useReenviarEntrega(webhookId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (entregaId: string) =>
      enviarSemConteudo(
        `/automacao/webhooks/${encodeURIComponent(webhookId)}/entregas/${encodeURIComponent(entregaId)}/reenviar`,
      ),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "webhooks", webhookId, "entregas"] }),
  });
}

export function useTokensDeApi(enabled = true) {
  return useQuery({
    queryKey: [...CHAVE, "tokens"],
    queryFn: ({ signal }) => pedirJson("/automacao/tokens", z.array(esquemaToken), { signal }),
    enabled,
  });
}

export function useCriarTokenDeApi() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: { nome: string; organizacao_id: string | null; escopos: string[] }) =>
      enviarJson("/automacao/tokens", esquemaTokenCriado, corpo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "tokens"] }),
  });
}

export function useRevogarTokenDeApi() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      enviarJson(`/automacao/tokens/${encodeURIComponent(id)}/revogar`, esquemaToken, {}),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "tokens"] }),
  });
}
